import { ref, computed, type Ref, type ComputedRef } from "vue";
import { captureException } from "@sentry/vue";
import { adminV1OrganizationsAssociatedContactsRetrieve } from "@/api/admin-associated-contacts/admin-associated-contacts";
import { adminV1OrganizationsPaymentsBillingProfilesRetrieve } from "@/api/admin-payments-billing-profiles/admin-payments-billing-profiles";
import type {
  AdminAssociatedContactDetail,
  AdminBillingProfileDetail,
} from "@/api/model";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import {
  buildStoredPaymentMethodOptions,
  type StoredPaymentMethodOption,
} from "@/modules/payments/utils/storedPaymentMethodOption";

// Both detail shapes expose `payment_methods` and `payabli_customer_number`,
// which is all the change-payment-method flow reads off `contactDetail`.
type ContactPaymentDetail =
  | AdminAssociatedContactDetail
  | AdminBillingProfileDetail;

interface UseContactPaymentMethodsReturn {
  contactDetail: Ref<ContactPaymentDetail | null>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  paymentMethodOptions: ComputedRef<StoredPaymentMethodOption[]>;
  payabliCustomerNumber: ComputedRef<string>;
  fetchContactPaymentMethods: (contactId: number | string) => Promise<void>;
}

// BillingProfile PKs are UUIDs; AssociatedContact PKs are integers. The id we
// receive can be either depending on where it originated (e.g. a registrant's
// `associated_contact` is now a BillingProfile with a UUID id), so route the
// fetch to the matching endpoint.
const isBillingProfileId = (id: number | string): id is string =>
  typeof id === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

export function useContactPaymentMethods(): UseContactPaymentMethodsReturn {
  const { currentOrganizationId } = useCurrentOrganization();
  const contactDetail = ref<ContactPaymentDetail | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const fetchContactPaymentMethods = async (contactId: number | string) => {
    if (!currentOrganizationId.value) return;

    isLoading.value = true;
    error.value = null;

    try {
      // A UUID means we already have the BillingProfile id — fetch it directly
      // and skip the AssociatedContact lookup (which only accepts integer PKs).
      if (isBillingProfileId(contactId)) {
        contactDetail.value =
          await adminV1OrganizationsPaymentsBillingProfilesRetrieve(
            currentOrganizationId.value,
            contactId
          );
        return;
      }

      const acResult = await adminV1OrganizationsAssociatedContactsRetrieve(
        currentOrganizationId.value,
        contactId
      );

      const billingProfileId =
        typeof acResult.billing_profile === "object" &&
        acResult.billing_profile !== null
          ? (acResult.billing_profile as unknown as { id: string }).id
          : acResult.billing_profile;

      // The BillingProfile is the only source of billing data (the AC's own
      // billing fields are being dropped from the backend), so without it the
      // contact keeps its identity fields but has no payment methods.
      let finalDetail: AdminAssociatedContactDetail = {
        ...acResult,
        payment_methods: [],
        payabli_customer_url: null,
        payabli_customer_number: null,
      };

      if (billingProfileId) {
        try {
          const bpDetail =
            await adminV1OrganizationsPaymentsBillingProfilesRetrieve(
              currentOrganizationId.value,
              billingProfileId
            );
          finalDetail = {
            ...acResult,
            payment_methods: bpDetail.payment_methods,
            payabli_customer_url: bpDetail.payabli_customer_url,
            payabli_customer_number: bpDetail.payabli_customer_number,
          };
        } catch (bpError) {
          console.warn(
            "[useContactPaymentMethods] Failed to fetch billing profile",
            bpError
          );
          captureException(bpError, {
            extra: {
              message: "Failed to fetch billing profile payment methods",
            },
          });
        }
      }

      contactDetail.value = finalDetail;
    } catch (err) {
      captureException(err, {
        extra: { message: "Failed to fetch contact payment methods" },
      });
      error.value = "Failed to load payment methods";
    } finally {
      isLoading.value = false;
    }
  };

  const paymentMethodOptions = computed<StoredPaymentMethodOption[]>(() => {
    if (!contactDetail.value) return [];

    return buildStoredPaymentMethodOptions(
      contactDetail.value.payment_methods || []
    );
  });

  const payabliCustomerNumber = computed(
    () => contactDetail.value?.payabli_customer_number || ""
  );

  return {
    contactDetail,
    isLoading,
    error,
    paymentMethodOptions,
    payabliCustomerNumber,
    fetchContactPaymentMethods,
  };
}
